import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UserContract, UserUpdateRequest } from '@app-types/api/user';
import { PermissionService } from '@app-services/permission/permission.service';
import { PermissionType } from '@app-types/enums/permission-type';
import { environment } from '@app-environments/environment';
import { OrganizationSettingsClient } from 'src/app/services/api/clients/organization-settings.client';
import { BaseGetByIdRequest } from 'src/app/types/base/base';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LOCALE_IDS, STORAGE_NAMES } from 'src/app/shared/constants/constants';
import { FormatAccountLanguage } from 'src/app/shared/pipes/format-account-language.pipe';
import { MatTabsModule } from '@angular/material/tabs';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { LocalStorageService } from '@app-services/local-storage.service';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { DrawerService } from '@app-services/drawer.service';
import { AccountUpdateDialogComponent } from '@app-components/account-update-dialog/account-update-dialog.component';
import { GeneralSettingsSkeletonComponent } from '@app-components/common/skeletons/general-settings-skeleton/general-settings-skeleton.component';

const { USER_LOCALE } = STORAGE_NAMES;

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  imports: [
    MatTabsModule,
    SomethingWentWrongComponent,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    FormatAccountLanguage,
    AccountUpdateDialogComponent,
    GeneralSettingsSkeletonComponent,
  ],
})
export class AccountSettingsComponent implements OnInit {
  public isLoading = false;
  public isUploaded = false;
  public isError = false;
  public iscredemISAMSupport = false;
  public user: UserContract;
  public canChangePassword = false;
  public organizationId: number;
  public locales = LOCALE_IDS;
  isCredem = environment.isCredem;

  @ViewChild('updateDrawer') updateDrawer: TemplateRef<any>;

  constructor(
    private permissionService: PermissionService,
    public matchError: MatchError,
    private organizationSettingsClient: OrganizationSettingsClient,
    public translate: TranslateService,
    private localStorageService: LocalStorageService,
    private userWorkspaceService: UserWorkspaceService,
    public drawerService: DrawerService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadUser();
  }

  public async loadUser(): Promise<void> {
    this.isUploaded = false;
    const loaderTimeout = setTimeout(() => {
      this.isLoading = true;
    }, 500);
    this.isError = false;
    try {
      const organizationId =
        this.userWorkspaceService.currentOrganization?.organizationId;
      if (organizationId) {
        const organizationSettings =
          await this.organizationSettingsClient.getByOrganization(
            new BaseGetByIdRequest(organizationId)
          );
        this.iscredemISAMSupport =
          organizationSettings.result.credemISAMSupport;
      }
      this.user = this.userWorkspaceService.user;
      await this.loadCanChangePassword();
    } catch (e) {
      this.isError = true;
      this.matchError.logError(e);
    } finally {
      clearTimeout(loaderTimeout);
      this.isUploaded = true;
      this.isLoading = false;
    }
  }
  public onChangePassword(): void {
    const fullURL = location.href;
    window.location.href = `${environment.apiOauthUrl}?returnUrl=${fullURL}`;
  }

  private async loadCanChangePassword(): Promise<void> {
    if (
      await this.permissionService.hasPermissionOver(
        null,
        null,
        PermissionType.CanEverything
      )
    ) {
      this.canChangePassword = false;
      return;
    }
    this.canChangePassword =
      !!this.userWorkspaceService.userOrganisaions?.length;
  }

  openUpdateUserDialog(): void {
    this.drawerService.openDrawer(this.updateDrawer);
  }

  async onUpdateUser(name: UserUpdateRequest): Promise<void> {
    this.drawerService.closeDrawer();
    this.user.firstName = name.firstName;
    this.user.lastName = name.lastName;
    this.user.localizationCode = name.localizationCode;
    this.user.signInOrganizationType = name.signInOrganizationType;
    this.user.preferredOrganizationId = name.preferredOrganizationId;
    this.localStorageService.setData(USER_LOCALE, this.user.localizationCode);
    this.user.localizationCode &&
      this.translate.use(this.user.localizationCode);
  }

  protected readonly environment = environment;
}
